import * as React from "react";
import { MessageContainer } from "source/components/framework/Message/MessageContainer";
import { MessageListContainer } from "source/components/framework/Message/MessageListContainer";
import { Base } from "ui/Scripts/source/framework/base";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IEventEditItem } from "source/models/event/eventEditItem";
import { EventEditItemMessage, IEventEditItemMessage } from "ui/Scripts/source/models/event/eventEditItemMessage";
import * as eventService from "source/models/services/eventService";
import { EventEditDialogMessageListLine } from "./EventEditDialogMessageListLine";
import { EventEditDialogMessageListNew } from "./EventEditDialogMessageListNew";

export interface IEventEditDialogMessageListProp {
    classes?: string;
    companyId: string;
    lateThreshold: number;
    isReadOnly: boolean;
    event: IEventEditItem;
    handlers: IModifyUser[];
    messages: IEventEditItemMessage[];
    maxEventMessageAttachmentSize: number;
    hideNewMessageInput?: boolean;
    onEventMessageSaved: (newMessage: IEventEditItemMessage) => void;
    onEventMessageRead: (id: string) => void;
}

interface IEventEditDialogMessageListState {
    selectedId: string;
    observer: IntersectionObserver;
    messages: IEventEditItemMessage[];
}

export class EventEditDialogMessageList extends React.Component<IEventEditDialogMessageListProp, IEventEditDialogMessageListState> {
    private messagesContainerDiv = React.createRef<HTMLDivElement>();
    private readReceiptProcessingIds: string[] = [];
    private readReceiptProcessedIds: string[] = [];

    intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        const props = this.props;
        for (const entry of entries) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.75) continue;
            const line = entry.target as HTMLDivElement;
            if (!line) continue;
            //line.style.backgroundColor = "#FFD800"
            const id = line.getAttribute("data-id");
            if (!id) return;
            const item = this.props.messages.find(i => i.id === id);
            if (!item || item.myMessage || item.isRead) continue;
            const processing = this.readReceiptProcessingIds.indexOf(id) > -1;
            if (processing) continue;
            const processed = this.readReceiptProcessedIds.indexOf(id) > -1;
            if (processed) continue;
            this.readReceiptProcessingIds.push(id);
            eventService.sendEventMessageReadReceipts(props.companyId, props.event.id, [id])
                .then(result => {
                    if (result) {
                        this.readReceiptProcessedIds.push(id);
                        props.onEventMessageRead(id);
                    }
                })
                .finally(() => {
                    this.readReceiptProcessingIds = this.readReceiptProcessingIds.filter(i => i !== id);
                });
        }
    };

    createObserver = (): IntersectionObserver => {
        const observer = new IntersectionObserver(this.intersectionCallback, {
            root: this.messagesContainerDiv.current,
            rootMargin: "0px",
            threshold: 0.9
        });
        this.setState({
            observer
        });
        return observer;
    };

    removeObserver = () => {
        const state = this.state;
        if (!state.observer) return;
        state.observer.disconnect();
        this.setState({
            observer: null
        });
    };

    scrollToBottom = () => {
        this.messagesContainerDiv.current.scrollTo({
            top: this.messagesContainerDiv.current.scrollHeight,
            left: 0,
            behavior: "auto"
        });
    };

    getMessages(props: IEventEditDialogMessageListProp): IEventEditItemMessage[] {
        const result = props.messages.map(i => new EventEditItemMessage(i)) ?? [];
        //Published
        if (props.event.publishUser) {
            const message = new EventEditItemMessage();
            message.modifyUser = props.event.publishUser;
            message.id = Base.getGuid();
            message.uiType = 1;
            result.push(message);
        }
        //Approved
        let closed = true;
        let lastApprovedHandler: IModifyUser = null;
        for (const handler of props.handlers) {
            if (!handler.date) {
                closed = false;
                continue;
            }
            const message = new EventEditItemMessage();
            message.modifyUser = handler;
            message.id = Base.getGuid();
            message.uiType = 2;
            result.push(message);
            if (closed && (!lastApprovedHandler || lastApprovedHandler.date < handler.date)) {
                lastApprovedHandler = handler;
            }
        }
        //Closed
        if (closed && lastApprovedHandler) {
            const message = new EventEditItemMessage();
            message.modifyUser = lastApprovedHandler;
            message.id = Base.getGuid();
            message.uiType = 3;
            result.push(message);
        }
        return EventEditItemMessage.sortEventEditItemMessages(result);
    }

    constructor(props: IEventEditDialogMessageListProp) {
        super(props);
        this.state = {
            observer: null,
            selectedId: null,
            messages: this.getMessages(props)
        };
    }

    componentDidMount(): void {
        //Observer
        this.createObserver();
        this.scrollToBottom();
    }

    componentWillUnmount(): void {
        //Observer
        this.removeObserver();
    }

    componentDidUpdate(prevProps: IEventEditDialogMessageListProp) {
        const props = this.props;
        const prevMessages = prevProps.messages ?? [];
        const messages = props.messages ?? [];
        if (prevMessages.map(i => i.id + "#" + i.content).join(", ") === messages.map(i => i.id + "#" + i.content).join(", ")) return;
        this.setState({
            messages: this.getMessages(props)
        });
    }
    
    handleMessageClick = (id: string) => {
        this.setState({
            selectedId: id
        });
    };

    handleEventMessageSaved = (item: IEventEditItemMessage) => {
        const props = this.props;
        props.onEventMessageSaved(item);
        window.setTimeout(() => {
            this.scrollToBottom();
        }, 50);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const messages = state.messages;
        return (
            <MessageContainer>
                <MessageListContainer ref={this.messagesContainerDiv}>
                    {messages.map((message) =>
                        <EventEditDialogMessageListLine
                            key={message.id}
                            observer={state.observer}
                            message={message}
                            selectedId={state.selectedId}
                            lateThreshold={props.lateThreshold}
                            onClick={this.handleMessageClick}
                        />
                    )}
                </MessageListContainer>
                {!props.hideNewMessageInput &&
                    <EventEditDialogMessageListNew
                        isReadOnly={props.isReadOnly}
                        companyId={props.companyId}
                        eventId={props.event.id}
                        maxEventMessageAttachmentSize={props.maxEventMessageAttachmentSize}
                        onEventMessageSaved={this.handleEventMessageSaved}
                    />
                }
            </MessageContainer>
        );
    }
}